import React, { useState, useEffect } from 'react';
import { 
    Button,
    SpaceBetween,
    Modal,
    Box,
    ProgressBar,
    Alert,
    ExpandableSection,
    Container,
    TextContent,
    Checkbox
  } from '@cloudscape-design/components';
import { submissionsApi } from 'api/Submission';


const DetailSummary = ({ request }) => (
    <Container>
      <SpaceBetween size="l">
        <div>
          <SpaceBetween size="s">
            <div>
              <strong>User:</strong> {request.username}
            </div>
            <div>
              <strong>Email:</strong> {request.user_email}
            </div>
            <div>
              <strong>Account:</strong> {request.target_account_name || request.target_account_id}
            </div>
            <div>
              <strong>Duration:</strong> {request.duration} hours
            </div>
            <div>
              <strong>Start:</strong> {new Date(request.start_datetime).toLocaleString()}
            </div>
            <div>
              <strong>End:</strong> {new Date(request.end_datetime).toLocaleString()}
            </div>
            {request.notes && (
              <div>
                <strong>Notes:</strong> {request.notes}
              </div>
            )}
          </SpaceBetween>
        </div>
  
        <div>
          <strong>Policies:</strong>
          <SpaceBetween size="s">
            {request.policies.map((policy, index) => (
              <ExpandableSection
                key={index}
                headerText={policy.title}
                headerDescription={`Type: ${policy.type}`}
              >
                <Box padding="s">
                  <pre className="whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(policy.json, null, 2)}
                  </pre>
                </Box>
              </ExpandableSection>
            ))}
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </Container>
  );
  
const BatchActionModal = ({ 
  visible, 
  onDismiss, 
  selectedItems, 
  setSelectedItems, 
  actionType, 
  onAction, 
  title,
  onResultsChange 
}) => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [requestDetails, setRequestDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [denyDelete, setDenyDelete] = useState(false);
  
  useEffect(() => {
    const fetchRequestDetails = async () => {
      if (visible && selectedItems.length > 0) {
        setLoading(true);
        try {
          const details = await Promise.all(
            selectedItems.map(item => 
              submissionsApi.getUserAccessDetails(item.id)
            )
          );
          setRequestDetails(details);
        } catch (error) {
          console.error('Error fetching request details:', error);
        }
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [visible, selectedItems]);

  const handleBatchAction = async () => {
    onResultsChange([]);
    setProcessing(true);
    setProgress(0);
    const results = [];
    
    const total = selectedItems.length;
    let completed = 0;

    for (const item of selectedItems) {
      try {
        const options = {};
        if (actionType === 'approve' && denyDelete) {
          options.deny_delete = true;
        }
        await onAction(actionType, item.id, options);
        results.push({ 
          id: item.id,
          success: true,
          message: `Successfully ${actionType}d request ${item.id}`
        });
      } catch (error) {
        let errorMessage = `Failed to ${actionType} request ${item.id}`;
        if (error.detail && error.detail.error_code === 3) {
          errorMessage = `Admins cannot approve their own requests, Request: ${item.id}`;
        }
        
        results.push({
          id: item.id,
          success: false,
          message: errorMessage
        });
      }
      
      completed++;
      setProgress((completed / total) * 100);
      onResultsChange(results);
    }
    setSelectedItems([]);
    setProcessing(false);
    setDenyDelete(false);
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button 
              variant="link" 
              onClick={onDismiss}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleBatchAction}
              disabled={processing}
            >
              {actionType.charAt(0).toUpperCase() + actionType.slice(1)}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <p>
          Are you sure you want to {actionType} the request(s)?
        </p>
        
        {actionType === 'approve' && (
          <Checkbox
            checked={denyDelete}
            onChange={({ detail }) => setDenyDelete(detail.checked)}
          >
            Deny Delete
          </Checkbox>
        )}
        
        {loading ? (
            <TextContent>Loading request details...</TextContent>
          ) : (
            <SpaceBetween size="s">
              {requestDetails.map((request, index) => (
                <ExpandableSection
                  key={request.id}
                  headerText={`Request ${request.username}`}
                >
                  <DetailSummary request={request} />
                </ExpandableSection>
              ))}
            </SpaceBetween>
          )}

        {processing && (
          <Box margin={{ top: 'l' }}>
            <ProgressBar 
              value={progress}
              label={`Processing requests...`}
            />
          </Box>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default BatchActionModal;